import { formatCurrency } from '@apparel/core/utils/strings/currency';
import { cx } from '@apparel/react/cx';
import { useService } from '@apparel/react/hooks/useService';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import Grow from '@material-ui/core/Grow';
import List from '@material-ui/core/List';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import InfoIcon from '@material-ui/icons/Info';
import StoreIcon from '@material-ui/icons/Store';
import React, { useCallback, useMemo, useRef } from 'react';
import { NewAuthDialog } from '../auth/AuthButtons/NewAuthDialog/NewAuthDialog';
import { NewAuthDialogState } from '../auth/AuthButtons/NewAuthDialog/NewAuthDialogContext';
import { NewAuthDialogProvider } from '../auth/AuthButtons/NewAuthDialog/NewAuthDialogProvider';
import { CartCheckout } from './CartCheckout';
import { CartContentItem } from './CartContentItem';
import { CartReset } from './CartReset';
import { CartItem } from './CartResult';
import { CartReview } from './CartReview';
import { CartService } from './CartService';
import { useCartItems } from './useCartItems';

export interface CartContentProps {}

const useStyles = makeStyles(
    (theme) => ({
        centeredContent: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            alignContent: 'center',
            flexDirection: 'column',
        },

        errorHolder: {
            padding: '40px 20px',

            '& .MuiSvgIcon-root': {
                fontSize: 48,
            },
        },

        holder: {
            backgroundColor: '#fff',
            borderRadius: 6,
            overflow: 'hidden',
            boxShadow: theme.shadows[8],
        },

        headerHeadline: {
            marginRight: 8,
        },

        list: {
            minHeight: 56,
        },

        loading: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
        },

        totalIcon: {
            width: 20,
            height: 20,
            margin: '0 0 0 4px',
        },

        totalText: {
            '& .MuiTypography-root': {
                lineHeight: '20px',
            },
        },

        textSpace: { marginRight: 4 },
        marginRight: { marginRight: 8 },
        footer: {
            flexWrap: 'wrap',

            [theme.breakpoints.down('xs')]: {
                '& .MuiBox-root': {
                    justifyContent: 'center',
                },

                '& .MuiButton-root': {
                    width: '100%',
                },
            },
        },
    }),
    {
        name: 'CartContent',
    },
);

export const CartContent = React.memo((_props: CartContentProps) => {
    const { loading, skus, packages, prices, count, hasPrices, hasItems, error, loadingActions } = useCartItems();
    const classes = useStyles();
    const cart$ = useService(CartService);
    const newDialogRef = useRef<NewAuthDialogState>(null);

    const handleDelete = useCallback(
        (...items: CartItem[]) =>
            () => {
                items.forEach((item) => {
                    cart$.removeItem(item);
                });
            },
        [],
    );

    const handleReset = useCallback(
        (before: boolean) => {
            if (before) {
                loadingActions.start();
            } else {
                loadingActions.stop();
            }
        },
        [loadingActions],
    );

    const items$ = useMemo<any[]>(() => {
        const list: any[] = [];

        skus.collection.forEach((item, index) => {
            list.push(
                <CartContentItem
                    key={`s_${index}`}
                    title={item.sku.name}
                    image={item.thumbnail}
                    quantity={item.quantity}
                    factory={item.factory?.name}
                    category={item.sku.category}
                    subcategory={item.sku.subcategory}
                    total={item.price.price}
                    price={item.price.price_single}
                    onDelete={handleDelete(item)}
                />,
            );
        });

        packages.collection.forEach((skuList, index) => {
            const quantity = skuList.getPackageQuantity();

            list.push(
                <CartContentItem
                    key={`p_${index}`}
                    title={skuList.package!.name}
                    image={skuList.thumbnail}
                    factory={skuList.item.factory?.name}
                    quantity={quantity}
                    total={skuList.price?.price ?? 0}
                    price={skuList.price?.price_single ?? 0}
                    onDelete={handleDelete(...skuList.collection)}
                    isPackage
                >
                    <List>
                        {skuList.collection.map((item, index) => (
                            <CartContentItem
                                key={`ps_${index}`}
                                title={item.sku.name}
                                image={item.sku.preview_photo?.thumbnail_path ?? item.thumbnail}
                                quantity={item.quantity}
                                inner
                            />
                        ))}
                    </List>
                </CartContentItem>,
            );
        });

        return list;
    }, [skus, packages, loading]);

    if (error) {
        return (
            <Box className={cx(classes.holder, classes.centeredContent, classes.errorHolder)}>
                <ErrorOutlineIcon color={'disabled'} />
                <Typography variant={'subtitle2'} color={'textSecondary'} align={'center'}>
                    Something went wrong, items couldn't be loaded!
                </Typography>
                <Box display={'inline-flex'} marginTop={2}>
                    <CartReset
                        variant={'outlined'}
                        color={'primary'}
                        size={'small'}
                        onReset={handleReset}
                        className={classes.marginRight}
                    />
                    <CartReview size={'small'} />
                </Box>
            </Box>
        );
    }

    if (!loading && !hasItems) {
        return (
            <Box className={cx(classes.holder, classes.centeredContent, classes.errorHolder)}>
                <StoreIcon color={'disabled'} />
                <Typography variant={'subtitle2'} color={'textSecondary'} align={'center'}>
                    You don't have any products!
                </Typography>
                <Box display={'inline-flex'} marginTop={2}>
                    <CartReview size={'small'} />
                </Box>
            </Box>
        );
    }

    return (
        <Box className={classes.holder} display={'flex'} flexDirection={'column'}>
            <Box display={'flex'} alignItems={'center'} padding={'12px 14px'}>
                <Typography variant={'subtitle2'} className={classes.headerHeadline}>
                    Your Cart
                </Typography>
                {hasItems && (
                    <Typography variant={'caption'} color={'textSecondary'}>
                        ({count} items)
                    </Typography>
                )}
                {hasItems && hasPrices && (
                    <Box
                        display={'flex'}
                        alignItems={'center'}
                        alignContent={'center'}
                        justifyContent={'flex-end'}
                        flex={'1 1 auto'}
                        className={classes.totalText}
                    >
                        <Typography variant={'caption'} color={'textSecondary'} className={classes.textSpace}>
                            Total:
                        </Typography>
                        <Typography variant={'caption'} color={'primary'}>
                            {formatCurrency(prices?.total, { maximumFractionDigits: 4 })}
                        </Typography>
                        <Tooltip title={"Total it's including other costs, review your cart for more details."}>
                            <InfoIcon color={'disabled'} className={classes.totalIcon} />
                        </Tooltip>
                    </Box>
                )}
            </Box>
            <Divider />
            <Box position={'relative'} maxHeight={300} overflow={'auto'}>
                <Grow in={loading} unmountOnExit>
                    <Box
                        padding={2}
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        className={classes.loading}
                    >
                        <CircularProgress size={24} />
                    </Box>
                </Grow>
                <List className={classes.list}>{items$}</List>
            </Box>
            <Divider />
            <Box
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
                padding={'0 0 10px'}
                className={classes.footer}
            >
                <Box
                    display={'inline-flex'}
                    alignItems={'center'}
                    justifyContent={'flex-start'}
                    flex={'1 1 auto'}
                    padding={'10px 10px 0'}
                >
                    <CartReview variant={'outlined'} size={'small'} />
                </Box>
                <Box
                    display={'inline-flex'}
                    alignItems={'center'}
                    justifyContent={'flex-end'}
                    flex={'1 1 auto'}
                    padding={'10px 10px 0'}
                >
                    <NewAuthDialogProvider
                        apiRef={newDialogRef}
                        cartItems={{ sku: skus.collection, packages: packages.collection }}
                        totalAmount={formatCurrency(prices?.total, { maximumFractionDigits: 4 })}
                        fromCartCheckout
                    >
                        <CartCheckout size={'small'} />

                        <NewAuthDialog view={'signUp'} title={'Complete Checkout'} />
                    </NewAuthDialogProvider>
                </Box>
            </Box>
        </Box>
    );
});

CartContent.displayName = 'CartContent';

export default CartContent;
